import { z } from "zod";

/**
 * Error-only schema for Media form (blocking validation)
 *
 * Contains only required field validation and business rules.
 * This validation blocks form submission.
 */
export const MediaErrorSchema = z.object({
  files: z.array(z.unknown()).min(1, "At least one photo is required"),
});

/**
 * Warning-only schema for Media form (non-blocking recommendations)
 *
 * Contains UX recommendations that don't block submission.
 * These are shown immediately to guide users toward best practices.
 */
export const MediaWarningSchema = z
  .object({
    files: z.array(z.unknown()),
  })
  .superRefine((data, ctx) => {
    // CLIENT WARNING: Recommend more photos
    if (data.files && data.files.length < 5) {
      ctx.addIssue({
        code: "custom",
        message: "Consider adding at least 5 photos to showcase your property.",
        severity: "warning",
        path: ["files"],
      });
    }
  })
  .superRefine((data, ctx) => {
    // CLIENT WARNING: Recommend video content
    const hasVideo = data.files?.some(
      (file: unknown) =>
        typeof file === "object" &&
        file !== null &&
        "type" in file &&
        typeof file.type === "string" &&
        file.type.startsWith("video/"),
    );

    if (!hasVideo && data.files && data.files.length >= 3) {
      ctx.addIssue({
        code: "custom",
        message:
          "Consider adding a virtual tour or video to showcase your property.",
        severity: "warning",
        path: ["files"],
      });
    }
  });

export type MediaFormData = z.infer<typeof MediaErrorSchema>;
